/**
 * SimilarPlayersTabContainer - Logic container for Similar Players tab
 *
 * Manages GraphQL data fetching, filter state, and watchlist interactions
 * for the Similar Players tab on the player detail page.
 * @remarks This is a Container component - handles all business logic and state
 */

import { NetworkStatus } from "@apollo/client";
import React, {
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import SimilarPlayersList from "@/features/player-detail/components/organisms/SimilarPlayersList";
import { useSimilarPlayersListRenderers } from "@/features/player-detail/hooks/useSimilarPlayersListRenderers";

import SimilarPlayersTabView from "./SimilarPlayersTabView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import useMobileScreen from "@/hooks/useMobileScreen";
import {
  DEFAULT_MAX_AGE,
  DEFAULT_MAX_COST,
  DEFAULT_MAX_MINUTES_PLAYED,
  DEFAULT_MAX_MONTHS_REMAINING,
  DEFAULT_MIN_AGE,
  DEFAULT_MIN_COST,
  DEFAULT_MIN_MINUTES_PLAYED,
  DEFAULT_MIN_MONTHS_REMAINING,
} from "@/features/similar-players/stores/filterValues";
import {
  PlayerDetailPlayerMetaFragment,
  PlayerSearchPlayerFragment,
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
  SimilarPlayerSortField,
  SimilarPlayersPlayerResultFragment,
  SortOrder,
  useRosterManagementListMyOrganizationsLeaguesQuery,
  useRosterManagementListNationalitiesQuery,
  useSimilarPlayersListSimilarPlayersQuery,
} from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import {
  SimilarPlayersFilterState,
  SimilarPlayersSortOption,
} from "@/features/player-detail/types";
import {
  deriveOptionsState,
  deriveSimilarPlayersListState,
} from "@/features/player-detail/utils/similarPlayersQueryState";

/**
 * Sort options for the similar players list
 */
const SORT_OPTIONS: readonly SimilarPlayersSortOption[] = [
  {
    id: "similarity",
    label: "Similarity",
    field: SimilarPlayerSortField.SimilarityScore,
  },
  { id: "gpr", label: "GPR", field: SimilarPlayerSortField.Gpr },
  { id: "gpm", label: "GPM", field: SimilarPlayerSortField.Gpm },
  { id: "fit", label: "Player Style", field: SimilarPlayerSortField.FitScore },
  {
    id: "teamFit",
    label: "Team Style",
    field: SimilarPlayerSortField.TeamStyleFit,
  },
  {
    id: "carrying",
    label: "Carrying",
    field: SimilarPlayerSortField.Carrying,
  },
  {
    id: "dribbling",
    label: "Dribbling",
    field: SimilarPlayerSortField.Dribbling,
  },
  { id: "passing", label: "Passing", field: SimilarPlayerSortField.Passing },
  {
    id: "shooting",
    label: "Shooting",
    field: SimilarPlayerSortField.Shooting,
  },
  {
    id: "gkPassing",
    label: "GK Passing",
    field: SimilarPlayerSortField.GkPassing,
  },
  {
    id: "gkPositioning",
    label: "Positioning",
    field: SimilarPlayerSortField.GkPositioning,
  },
  {
    id: "gkShotStopping",
    label: "Shot Stopping",
    field: SimilarPlayerSortField.GkShotStopping,
  },
  {
    id: "playerValuation",
    label: "Transfer Value",
    field: SimilarPlayerSortField.PlayerValuation,
  },
  {
    id: "monthsRemaining",
    label: "Months Remaining",
    field: SimilarPlayerSortField.MonthsRemaining,
  },
] as const;

/**
 * Default filter state values
 */
const DEFAULT_FILTER_STATE: SimilarPlayersFilterState = {
  sortField: SimilarPlayerSortField.SimilarityScore,
  sortOrder: SortOrder.Desc,
  leagueIds: [],
  nationalityIds: [],
  ageRange: [DEFAULT_MIN_AGE, DEFAULT_MAX_AGE],
  costRange: [DEFAULT_MIN_COST, DEFAULT_MAX_COST],
  monthsRemainingRange: [
    DEFAULT_MIN_MONTHS_REMAINING,
    DEFAULT_MAX_MONTHS_REMAINING,
  ],
  minutesPlayedRange: [DEFAULT_MIN_MINUTES_PLAYED, DEFAULT_MAX_MINUTES_PLAYED],
} as const;

/**
 * Props for SimilarPlayersTabContainer component
 * Matches TabContentProps interface from PlayerPageTabContentContainer
 */
interface SimilarPlayersTabContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
}

/**
 * Container component that manages Similar Players tab data and interactions
 * @param root0 - Component props
 * @param root0.isDark - Whether dark mode is active
 * @param root0.player - Player data fragment
 * @returns SimilarPlayersTabView component
 */
const SimilarPlayersTabContainer = ({
  isDark,
  player,
}: SimilarPlayersTabContainerProps) => {
  const playerId = player?.id ?? "";
  const isMobile = useMobileScreen();
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Filter state - committed values used for queries
  const [filterState, setFilterState] =
    useState<SimilarPlayersFilterState>(DEFAULT_FILTER_STATE);

  // Pending values - track uncommitted changes that require Save
  const [pendingSortField, setPendingSortField] = useState(
    DEFAULT_FILTER_STATE.sortField
  );
  const [pendingSortOrder, setPendingSortOrder] = useState(
    DEFAULT_FILTER_STATE.sortOrder
  );
  const [pendingLeagueIds, setPendingLeagueIds] = useState<readonly string[]>(
    []
  );
  const [pendingNationalityIds, setPendingNationalityIds] = useState<
    readonly string[]
  >([]);
  const [pendingAgeRange, setPendingAgeRange] = useState<
    readonly [number, number]
  >(filterState.ageRange);
  const [pendingCostRange, setPendingCostRange] = useState<
    readonly [number, number]
  >(filterState.costRange);
  const [pendingMonthsRange, setPendingMonthsRange] = useState<
    readonly [number, number]
  >(filterState.monthsRemainingRange);
  const [pendingMinutesRange, setPendingMinutesRange] = useState<
    readonly [number, number]
  >(filterState.minutesPlayedRange);

  // Watchlist action sheet state
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<
    PlayerSearchPlayerFragment | undefined
  >(undefined);
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as RefObject<CustomPromptRefProps>;

  const { handleAddWatchlistPress } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });

  // Fetch leagues for filter dropdown. SE-7580: `error` and `refetch` are
  // read so a failed leagues read renders as a retryable error instead of an
  // empty picker that reads as "your org has no leagues".
  const {
    data: leagueData,
    loading: loadingLeagues,
    error: leaguesError,
    refetch: refetchLeagues,
  } = useRosterManagementListMyOrganizationsLeaguesQuery({
    variables: { first: 100 },
  });

  const groupedLeagues = useMemo(
    () =>
      [...(leagueData?.listMyOrganizationsLeagues.edges ?? [])]
        .filter(league => league.node.name !== "")
        .sort((current, next) => {
          const countryCompare = (current.node?.country ?? "").localeCompare(
            next.node?.country ?? ""
          );
          if (countryCompare !== 0) {
            return countryCompare;
          }
          return current.node.name.localeCompare(next.node.name);
        })
        .map(
          e =>
            ({
              id: e.node.id,
              name: `${e.node.country} - ${e.node.name}`,
            }) as RosterManagementLeagueFragment
        ),
    [leagueData?.listMyOrganizationsLeagues.edges]
  );

  // Selected leagues derived from pending IDs for filter panel display
  const selectedLeagues = useMemo(
    () => groupedLeagues.filter(league => pendingLeagueIds.includes(league.id)),
    [groupedLeagues, pendingLeagueIds]
  );

  // Fetch nationalities for filter dropdown (SE-7580: same error handling as
  // the leagues query above).
  const {
    data: nationalityData,
    loading: loadingNationalities,
    error: nationalitiesError,
    refetch: refetchNationalities,
  } = useRosterManagementListNationalitiesQuery({
    variables: { first: 1000 },
  });

  const nationalities = useMemo(
    () =>
      [...(nationalityData?.listNationalities?.edges ?? [])]
        .map(edge => edge.node)
        .sort((current, next) => current.name.localeCompare(next.name)),
    [nationalityData?.listNationalities?.edges]
  );

  // Selected nationalities derived from pending IDs for filter panel display
  const selectedNationalities = useMemo(
    () =>
      nationalities.filter(nationality =>
        pendingNationalityIds.includes(nationality.id)
      ),
    [nationalities, pendingNationalityIds]
  );

  // SE-7580: picker states, so "the read failed" is no longer rendered as
  // "there are no options".
  const leaguesState = useMemo(
    () =>
      deriveOptionsState<RosterManagementLeagueFragment>({
        options: groupedLeagues,
        isLoading: loadingLeagues,
        hasError: Boolean(leaguesError),
      }),
    [groupedLeagues, loadingLeagues, leaguesError]
  );

  const nationalitiesState = useMemo(
    () =>
      deriveOptionsState<RosterManagementNationalityFragment>({
        options: nationalities,
        isLoading: loadingNationalities,
        hasError: Boolean(nationalitiesError),
      }),
    [nationalities, loadingNationalities, nationalitiesError]
  );

  // Query variables for similar players
  const queryVariables = useMemo(
    () => ({
      playerId,
      filter: {
        leagueIds:
          filterState.leagueIds.length > 0
            ? [...filterState.leagueIds]
            : undefined,
        nationalityIds:
          filterState.nationalityIds.length > 0
            ? [...filterState.nationalityIds]
            : undefined,
        minAge: filterState.ageRange[0],
        maxAge: filterState.ageRange[1],
        // SE-6496: the Cost filter binds the GPV midpoint, not the legacy
        // Transfermarkt valuation. An active bound excludes players without a
        // GPV, so the bounds are omitted while the range sits at its default —
        // an untouched filter must leave results unchanged.
        ...(filterState.costRange[0] !== DEFAULT_FILTER_STATE.costRange[0] ||
        filterState.costRange[1] !== DEFAULT_FILTER_STATE.costRange[1]
          ? {
              minGeminiPlayerValuation: filterState.costRange[0],
              maxGeminiPlayerValuation: filterState.costRange[1],
            }
          : {}),
        minMonthsRemaining: filterState.monthsRemainingRange[0],
        maxMonthsRemaining: filterState.monthsRemainingRange[1],
        minMinutesPlayed: filterState.minutesPlayedRange[0],
        maxMinutesPlayed: filterState.minutesPlayedRange[1],
        sortBy: filterState.sortField,
        sortOrder: filterState.sortOrder,
      },
    }),
    [playerId, filterState]
  );

  // Cache default filter results for fast tab switching; skip cache for custom
  // filters to avoid stale data flashes when the user adjusts sliders.
  const isCommittedDefault = useMemo(
    () =>
      filterState.sortField === DEFAULT_FILTER_STATE.sortField &&
      filterState.sortOrder === DEFAULT_FILTER_STATE.sortOrder &&
      filterState.leagueIds.length === 0 &&
      filterState.nationalityIds.length === 0 &&
      filterState.ageRange[0] === DEFAULT_FILTER_STATE.ageRange[0] &&
      filterState.ageRange[1] === DEFAULT_FILTER_STATE.ageRange[1] &&
      filterState.costRange[0] === DEFAULT_FILTER_STATE.costRange[0] &&
      filterState.costRange[1] === DEFAULT_FILTER_STATE.costRange[1] &&
      filterState.monthsRemainingRange[0] ===
        DEFAULT_FILTER_STATE.monthsRemainingRange[0] &&
      filterState.monthsRemainingRange[1] ===
        DEFAULT_FILTER_STATE.monthsRemainingRange[1] &&
      filterState.minutesPlayedRange[0] ===
        DEFAULT_FILTER_STATE.minutesPlayedRange[0] &&
      filterState.minutesPlayedRange[1] ===
        DEFAULT_FILTER_STATE.minutesPlayedRange[1],
    [filterState]
  );
  const fetchPolicy = isCommittedDefault ? "cache-and-network" : "network-only";

  // Fetch similar players. SE-7580: `error` is read here — it never was, which
  // is why a failed read rendered as "no similar players found".
  const {
    data,
    loading: queryLoading,
    error: listError,
    fetchMore,
    refetch,
    networkStatus,
  } = useSimilarPlayersListSimilarPlayersQuery({
    variables: queryVariables,
    fetchPolicy,
    nextFetchPolicy: fetchPolicy,
    notifyOnNetworkStatusChange: true,
    skip: !playerId,
  });

  const isRefreshing = networkStatus === NetworkStatus.refetch;

  // Transform similar players data for display
  const similarPlayers = useMemo(
    () =>
      (data?.listSimilarPlayers.edges ?? [])
        .map(e => e.node)
        .filter(item => !!item.player && item.player?.id !== playerId),
    [data?.listSimilarPlayers.edges, playerId]
  );

  // SE-7580: every in-flight window counts as "not resolved yet". The previous
  // check was `networkStatus === NetworkStatus.loading`, which is true only on
  // the very first fetch — so a filter change (`setVariables`) and a refetch
  // both fell through to the empty state and flashed "No similar players
  // found" before the real answer arrived. `notifyOnNetworkStatusChange` keeps
  // `queryLoading` true across all of those. The `!playerId` arm covers the
  // skipped query, where an absent result is not evidence of zero results.
  const isPending = !playerId || queryLoading;

  const listState = useMemo(
    () =>
      deriveSimilarPlayersListState({
        players: similarPlayers,
        isPending,
        hasError: Boolean(listError),
      }),
    [similarPlayers, isPending, listError]
  );

  const endCursor = useMemo(
    () =>
      data?.listSimilarPlayers?.pageInfo.hasNextPage
        ? data?.listSimilarPlayers?.pageInfo.endCursor
        : null,
    [
      data?.listSimilarPlayers?.pageInfo.endCursor,
      data?.listSimilarPlayers?.pageInfo.hasNextPage,
    ]
  );

  // Whether any MANUAL-APPLY filter (the sliders) has uncommitted edits.
  // Drives Save enablement (AC: Save shows only for manual changes).
  // Sort field, sort order, league, and nationality are AUTO-APPLY controls
  // that commit immediately, so by construction pending always equals
  // committed for them — they are deliberately excluded here so a
  // sort/league/nationality change never enables Save (SE-4617, SE-6210).
  const hasPendingChanges = useMemo(
    () =>
      pendingAgeRange[0] !== filterState.ageRange[0] ||
      pendingAgeRange[1] !== filterState.ageRange[1] ||
      pendingCostRange[0] !== filterState.costRange[0] ||
      pendingCostRange[1] !== filterState.costRange[1] ||
      pendingMonthsRange[0] !== filterState.monthsRemainingRange[0] ||
      pendingMonthsRange[1] !== filterState.monthsRemainingRange[1] ||
      pendingMinutesRange[0] !== filterState.minutesPlayedRange[0] ||
      pendingMinutesRange[1] !== filterState.minutesPlayedRange[1],
    [
      pendingAgeRange,
      pendingCostRange,
      pendingMonthsRange,
      pendingMinutesRange,
      filterState.ageRange,
      filterState.costRange,
      filterState.monthsRemainingRange,
      filterState.minutesPlayedRange,
    ]
  );

  // Get display label for pending sort option (shown in filter panel)
  const selectedSortLabel = useMemo(
    () => SORT_OPTIONS.find(opt => opt.field === pendingSortField)?.label ?? "",
    [pendingSortField]
  );

  // Check if filters are at default values (uses pending values for display)
  const isDefaultFilters = useMemo(
    () =>
      pendingSortField === DEFAULT_FILTER_STATE.sortField &&
      pendingSortOrder === DEFAULT_FILTER_STATE.sortOrder &&
      pendingLeagueIds.length === 0 &&
      pendingNationalityIds.length === 0 &&
      pendingAgeRange[0] === DEFAULT_FILTER_STATE.ageRange[0] &&
      pendingAgeRange[1] === DEFAULT_FILTER_STATE.ageRange[1] &&
      pendingCostRange[0] === DEFAULT_FILTER_STATE.costRange[0] &&
      pendingCostRange[1] === DEFAULT_FILTER_STATE.costRange[1] &&
      pendingMonthsRange[0] === DEFAULT_FILTER_STATE.monthsRemainingRange[0] &&
      pendingMonthsRange[1] === DEFAULT_FILTER_STATE.monthsRemainingRange[1] &&
      pendingMinutesRange[0] === DEFAULT_FILTER_STATE.minutesPlayedRange[0] &&
      pendingMinutesRange[1] === DEFAULT_FILTER_STATE.minutesPlayedRange[1],
    [
      pendingSortField,
      pendingSortOrder,
      pendingLeagueIds,
      pendingNationalityIds,
      pendingAgeRange,
      pendingCostRange,
      pendingMonthsRange,
      pendingMinutesRange,
    ]
  );

  // AUTO-APPLY handlers (sort field, sort order, league): commit to BOTH the
  // pending mirror AND the committed `filterState` immediately so the query
  // refires with exactly one new variable set. The functional `filterState`
  // update preserves the currently-committed MANUAL values (age/cost/months/
  // minutes/nationalities) so an auto-apply never resets them, and the pending
  // MANUAL state is left untouched so in-progress slider edits survive (AC:
  // pending edits persist across auto-apply) (SE-4617).
  const handleSortFieldChange = useCallback((field: SimilarPlayerSortField) => {
    setPendingSortField(field);
    setFilterState(prev => ({ ...prev, sortField: field }));
  }, []);

  const handleSortOrderToggle = useCallback(() => {
    setPendingSortOrder(prev =>
      prev === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc
    );
    setFilterState(prev => ({
      ...prev,
      sortOrder:
        prev.sortOrder === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc,
    }));
  }, []);

  const handleLeaguesChange = useCallback(
    (leagues: readonly RosterManagementLeagueFragment[]) => {
      const leagueIds = leagues.map(l => l.id);
      setPendingLeagueIds(leagueIds);
      setFilterState(prev => ({ ...prev, leagueIds: [...leagueIds] }));
    },
    []
  );

  // Nationality is AUTO-APPLY (SE-6210): mirrors the league handler above so a
  // selection commits and refires immediately, with no Save click required.
  const handleNationalitiesChange = useCallback(
    (
      selectedNationalitiesValue: readonly RosterManagementNationalityFragment[]
    ) => {
      const nationalityIds = selectedNationalitiesValue.map(n => n.id);
      setPendingNationalityIds(nationalityIds);
      setFilterState(prev => ({
        ...prev,
        nationalityIds: [...nationalityIds],
      }));
    },
    []
  );

  // MANUAL-APPLY handlers (sliders): update pending state only; committed on
  // Save.

  const handleAgeRangeChange = useCallback((range: [number, number]) => {
    setPendingAgeRange(range);
  }, []);

  const handleCostRangeChange = useCallback((range: [number, number]) => {
    setPendingCostRange(range);
  }, []);

  const handleMonthsRemainingRangeChange = useCallback(
    (range: [number, number]) => {
      setPendingMonthsRange(range);
    },
    []
  );

  const handleMinutesPlayedRangeChange = useCallback(
    (range: [number, number]) => {
      setPendingMinutesRange(range);
    },
    []
  );

  // Save commits the pending MANUAL values (the sliders) into the committed
  // filter state while PRESERVING the already-committed AUTO-APPLY values
  // (sort/order/league/nationality), then closes the mobile filter modal
  // (SE-4617, SE-6210).
  const handleSaveFilters = useCallback(() => {
    setFilterState(prev => ({
      ...prev,
      ageRange: pendingAgeRange,
      costRange: pendingCostRange,
      monthsRemainingRange: pendingMonthsRange,
      minutesPlayedRange: pendingMinutesRange,
    }));
    setShowFilterModal(false);
  }, [
    pendingAgeRange,
    pendingCostRange,
    pendingMonthsRange,
    pendingMinutesRange,
  ]);

  // Reset both the committed query state AND all pending state to defaults,
  // firing the default GPR-desc query.
  const handleResetFilters = useCallback(() => {
    setFilterState(DEFAULT_FILTER_STATE);
    setPendingSortField(DEFAULT_FILTER_STATE.sortField);
    setPendingSortOrder(DEFAULT_FILTER_STATE.sortOrder);
    setPendingLeagueIds([]);
    setPendingNationalityIds([]);
    setPendingAgeRange(DEFAULT_FILTER_STATE.ageRange);
    setPendingCostRange(DEFAULT_FILTER_STATE.costRange);
    setPendingMonthsRange(DEFAULT_FILTER_STATE.monthsRemainingRange);
    setPendingMinutesRange(DEFAULT_FILTER_STATE.minutesPlayedRange);
  }, []);

  // List interaction handlers. The `catch` on each of these only prevents an
  // unhandled promise rejection — the outcome itself is read off the hook's
  // `error` and surfaced through `listState` (SE-7580).
  const handleRefresh = useCallback(() => {
    refetch(queryVariables).catch(() => {});
  }, [refetch, queryVariables]);

  const handleLoadMore = useCallback(() => {
    if (endCursor && !queryLoading) {
      fetchMore({
        variables: { ...queryVariables, after: endCursor },
      }).catch(() => {});
    }
  }, [endCursor, queryLoading, fetchMore, queryVariables]);

  const handleRetryLeagues = useCallback(() => {
    refetchLeagues().catch(() => {});
  }, [refetchLeagues]);

  const handleRetryNationalities = useCallback(() => {
    refetchNationalities().catch(() => {});
  }, [refetchNationalities]);

  // Player row handlers
  const handleAddPress = useCallback(
    (player: SimilarPlayersPlayerResultFragment) => {
      setSelectedPlayer(player.player as PlayerSearchPlayerFragment);
      setShowActionSheet(true);
    },
    []
  );

  const handleRowPress = useCallback((playerId: string) => {
    // A seam, not a no-op: `useSimilarPlayersListRenderers` calls this before
    // it navigates, so anything the tab wants to do on a row press (analytics,
    // selection) has a place to go. The navigation itself lives in that hook
    // — it used to say "the View component" here, which stopped being true in
    // SE-8019.
    return playerId;
  }, []);

  const handleCloseActionSheet = useCallback(() => {
    setShowActionSheet(false);
    setSelectedPlayer(undefined);
  }, []);

  const handleOpenFilterModal = useCallback(() => {
    setShowFilterModal(true);
  }, []);

  const handleCloseFilterModal = useCallback(() => {
    setShowFilterModal(false);
  }, []);

  /**
   * Derives the tab-relative base path from the current pathname so that
   * navigation stays within the same tab stack (players, search, or watchlist).
   * e.g. "/search/players/abc" → "/search/players", "/players/abc" → "/players"
   */
  const { renderPlayerList } = useSimilarPlayersListRenderers({
    isMobile,
    isDark,
    committedSortField: filterState.sortField,
    refreshing: isRefreshing,
    onAddPress: handleAddPress,
    onRowPress: handleRowPress,
    onRefresh: handleRefresh,
    onLoadMore: handleLoadMore,
  });

  const listContent = (
    <SimilarPlayersList
      listState={listState}
      isDark={isDark}
      isRefreshing={isRefreshing}
      onRetry={handleRefresh}
      renderPlayerList={renderPlayerList}
    />
  );

  // Written longhand: the panel's prop names and this Container's local names
  // are two vocabularies, and shorthand was hiding that.
  const filterPanelProps = {
    isDark,
    sortOptions: SORT_OPTIONS,
    selectedSortField: pendingSortField,
    sortOrder: pendingSortOrder,
    leaguesState,
    selectedLeagues,
    nationalitiesState,
    selectedNationalities,
    ageRange: pendingAgeRange,
    costRange: pendingCostRange,
    monthsRemainingRange: pendingMonthsRange,
    minutesPlayedRange: pendingMinutesRange,
    hasPendingChanges,
    selectedSortLabel,
    isDefaultFilters,
    onSortFieldChange: handleSortFieldChange,
    onSortOrderToggle: handleSortOrderToggle,
    onLeaguesChange: handleLeaguesChange,
    onNationalitiesChange: handleNationalitiesChange,
    onAgeRangeChange: handleAgeRangeChange,
    onCostRangeChange: handleCostRangeChange,
    onMonthsRemainingRangeChange: handleMonthsRemainingRangeChange,
    onMinutesPlayedRangeChange: handleMinutesPlayedRangeChange,
    onRetryLeagues: handleRetryLeagues,
    onRetryNationalities: handleRetryNationalities,
    onSaveFilters: handleSaveFilters,
    onResetFilters: handleResetFilters,
  } as const;

  return (
    <SimilarPlayersTabView
      isDark={isDark}
      isMobile={isMobile}
      listState={listState}
      refreshing={isRefreshing}
      sortOptions={SORT_OPTIONS}
      selectedSortField={pendingSortField}
      committedSortField={filterState.sortField}
      sortOrder={pendingSortOrder}
      leaguesState={leaguesState}
      selectedLeagues={selectedLeagues}
      nationalitiesState={nationalitiesState}
      selectedNationalities={selectedNationalities}
      ageRange={pendingAgeRange}
      costRange={pendingCostRange}
      monthsRemainingRange={pendingMonthsRange}
      minutesPlayedRange={pendingMinutesRange}
      hasPendingChanges={hasPendingChanges}
      selectedSortLabel={selectedSortLabel}
      isDefaultFilters={isDefaultFilters}
      selectedPlayer={selectedPlayer}
      showActionSheet={showActionSheet}
      showFilterModal={showFilterModal}
      promptRef={promptRef}
      onSortFieldChange={handleSortFieldChange}
      onSortOrderToggle={handleSortOrderToggle}
      onLeaguesChange={handleLeaguesChange}
      onNationalitiesChange={handleNationalitiesChange}
      onAgeRangeChange={handleAgeRangeChange}
      onCostRangeChange={handleCostRangeChange}
      onMonthsRemainingRangeChange={handleMonthsRemainingRangeChange}
      onMinutesPlayedRangeChange={handleMinutesPlayedRangeChange}
      onRetryLeagues={handleRetryLeagues}
      onRetryNationalities={handleRetryNationalities}
      onSaveFilters={handleSaveFilters}
      onResetFilters={handleResetFilters}
      onRefresh={handleRefresh}
      onLoadMore={handleLoadMore}
      onAddPress={handleAddPress}
      onRowPress={handleRowPress}
      onCloseActionSheet={handleCloseActionSheet}
      onAddWatchlistPress={handleAddWatchlistPress}
      onOpenFilterModal={handleOpenFilterModal}
      onCloseFilterModal={handleCloseFilterModal}
      listContent={listContent}
      filterPanelProps={filterPanelProps}
    />
  );
};

SimilarPlayersTabContainer.displayName = "SimilarPlayersTabContainer";

export default SimilarPlayersTabContainer;
